// Neon Kabuki 2089 visual style: the single source for colors, fonts and crew mask colors in code.
// Rules and rationale live in docs/STYLE.md (reference: Visual Direction 02). Don't add colors here
// without updating that file. Contrast ratios below are measured against Midnight unless noted.

/** The six palette colors, by name (state colors are in STATE below). Prefer COLOR (by role) in UI and drawing code. */
export const PALETTE = {
  midnight: '#09121B',
  slate: '#3A4A5C',
  offWhite: '#F2EDE7',
  cyan: '#48E8FD',
  pink: '#FC3F92',
  amber: '#FDB453',
} as const;

/** State colors, added 2026-10-03 (owner approved). Only for game state, never decoration; always pair with a shape, icon or label. */
export const STATE = {
  danger: '#FF4A3D',
  warning: '#FFD93D',
  positive: '#4DEB8A',
} as const;

/** Colors by the job they do. */
export const COLOR = {
  /** Backgrounds and ground. */
  ground: PALETTE.midnight,
  /** Borders and panels only, never text (2.1:1 on Midnight). */
  border: PALETTE.slate,
  /** All text (16.2:1 on Midnight). Never on pink (2.9:1). */
  text: PALETTE.offWhite,
  /** Anything the player can press or interact with (12.8:1). */
  interactive: PALETTE.cyan,
  /** The brand and its glow (5.6:1 on Midnight). */
  brand: PALETTE.pink,
  /** Text on a pink fill: Midnight, 5.6:1. */
  onBrand: PALETTE.midnight,
  /** The world: lanterns, streetlights, interiors (10.6:1). Never used for UI. */
  world: PALETTE.amber,
  /** Enemies, low health, police, failure (5.7:1 on Midnight). */
  danger: STATE.danger,
  /** Heat stars, objectives, money, the "you" marker (13.7:1). */
  warning: STATE.warning,
  /** Ready, healed, success, RAM (12.2:1). */
  positive: STATE.positive,
} as const;

export type ColorRole = keyof typeof COLOR;

/** Display for the wordmark and headlines; body for everything else (Chakra Petch 500 and 600). */
export const FONT = {
  display: '"Russo One", system-ui, sans-serif',
  body: '"Chakra Petch", system-ui, sans-serif',
  bodyWeight: 500,
  bodyWeightStrong: 600,
} as const;

/** Locally bundled SIL OFL fonts, available offline and without third-party requests. */
export const FONT_URL =
  './fonts/fonts.css';

/** Crew mask colors, one per player slot: P1 Kabuki, P2 Oni, P3 Kitsune, P4 Karasu-tengu. */
export const CREW_COLOR = {
  kabuki: PALETTE.pink,
  oni: PALETTE.cyan,
  kitsune: PALETTE.offWhite,
  karasu: PALETTE.amber,
} as const;

export type CrewId = keyof typeof CREW_COLOR;
